import React from 'react';
import { TagIcon } from '@heroicons/react/24/solid';
import classNames from 'classnames';
import { Tooltip } from 'react-tooltip';
import { CURRENCY_USD } from 'constants/currency';
import { EventCouponSourceEnum, EventCouponTypeEnum } from 'types/generated/client';
import {
  EventCoupon,
  OrderTotal,
  PLATFORM_FEE_CENTS,
  PROCESSING_FEE,
} from 'utils/shared/money/calculateEventOrderTotal';
import { convertUnitPriceToFormattedPrice } from 'utils/shared/money/convertUnitPriceToFormattedPrice';
import { pluralize } from 'utils/shared/pluralize';
import InfoCircle from 'svg/InfoCircle';

const TOOLTIP_ID_PLATFORM_FEE = 'order-item-list-platform-fee';
const TOOLTIP_ID_TAXES_FEE = 'order-item-list-taxes-fee';

interface OrderItemListProps {
  orderTotal: OrderTotal;
  appliedCoupon?: EventCoupon | null;
  headerCta?: string;
  showBack?: boolean;
  onBack?: () => void;
}

function OrderItemRow({
  label,
  amount,
  isTotal = false,
  tooltipId,
  tooltipContent,
  highlightLabel,
  labelTooltipId,
}: {
  label: string;
  amount: number;
  isTotal?: boolean;
  tooltipId?: string;
  tooltipContent?: string;
  highlightLabel?: string;
  labelTooltipId?: string;
}) {
  const priceDisplay = convertUnitPriceToFormattedPrice(amount, CURRENCY_USD).priceDisplay;

  return (
    <div className={'flex justify-between gap-4'}>
      <div className="flex min-w-0 flex-1 gap-1">
        <div className="flex flex-col gap-2">
          <span
            className={classNames(
              isTotal
                ? 'typography-product-subheading text-color-text-brand'
                : 'text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary',
            )}
          >
            {label}
          </span>
          {highlightLabel && (
            <span className="typography-product-body-highlight text-color-text-lightmode-primary dark:text-color-text-darkmode-primary">
              {highlightLabel}
            </span>
          )}
        </div>

        {/* </div> */}
        {tooltipId && tooltipContent && (
          <>
            <InfoCircle
              className="mt-1 h-4 w-4 shrink-0 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon"
              data-tooltip-id={tooltipId}
              aria-hidden
            />
            <Tooltip id={tooltipId} content={tooltipContent} place="bottom" />
          </>
        )}
      </div>
      <span
        className={classNames(
          'shrink-0',
          isTotal
            ? 'typography-product-subheading text-color-text-brand'
            : 'typography-product-body-highlight',
        )}
      >
        {priceDisplay}
      </span>
    </div>
  );
}

interface GroupDiscount {
  name: string;
  amount: number;
  source: EventCouponSourceEnum;
  couponType: EventCouponTypeEnum;
}

function buildGroupDiscountMap(
  coupon: EventCoupon | null | undefined,
  groups: { id: string; priceUnitAmount: number }[],
): Map<string, GroupDiscount> {
  const map = new Map<string, GroupDiscount>();
  if (!coupon) return map;

  const appliesToAllGroups = coupon.couponGroups.length === 0;
  const scopedGroupIds = new Set(coupon.couponGroups.map((cg) => cg.eventGroupId));
  const applicableGroups = groups.filter((g) => appliesToAllGroups || scopedGroupIds.has(g.id));
  const applicableTotal = applicableGroups.reduce((sum, g) => sum + g.priceUnitAmount, 0);

  applicableGroups.forEach((group) => {
    let amount = 0;
    if (coupon.couponType === EventCouponTypeEnum.PercentOff) {
      amount = Math.round(group.priceUnitAmount * ((coupon.percentOff ?? 0) / 100));
    } else if (applicableTotal > 0) {
      const totalDiscount =
        coupon.couponType === EventCouponTypeEnum.AmountOff
          ? Math.min(coupon.amountOff ?? 0, applicableTotal)
          : Math.max(0, applicableTotal - (coupon.fixedPrice ?? 0));
      amount = Math.round((totalDiscount * group.priceUnitAmount) / applicableTotal);
    }
    map.set(group.id, {
      name: coupon.name,
      amount,
      source: coupon.source,
      couponType: coupon.couponType,
    });
  });

  return map;
}

export default function OrderItemList({ orderTotal, appliedCoupon }: OrderItemListProps) {
  const {
    registrationGroups = [],
    registrationFee,
    processingFee,
    total,
    platformFeeUnitAmount = 0,
    discountUnitAmount = 0,
  } = orderTotal;

  const groupDiscountMap = buildGroupDiscountMap(appliedCoupon, registrationGroups);

  const hasEvents = registrationGroups.some((g) => g.priceUnitAmount);
  const hasRegistrationFee = !!registrationFee;
  const hasPlatformFee = platformFeeUnitAmount > 0;
  const hasProcessingFee = !!processingFee;
  const hasTotal = !!total;
  const hasGroupDiscounts = [...groupDiscountMap.values()].some((d) => d.amount > 0);
  const hasDiscount = discountUnitAmount > 0 && !hasGroupDiscounts;

  const platformFeeTooltip = `A flat $${(PLATFORM_FEE_CENTS / 100).toFixed(
    2,
  )} fee is used to support our platform and enhance your tournament experience.`;
  const taxesTooltip = `A ${
    PROCESSING_FEE * 100
  }% fee is applied to cover credit card processing and applicable taxes.`;

  return (
    <div className="space-y-ds-md border-b border-color-border-input-lightmode pb-ds-xl dark:border-color-border-input-darkmode">
      {/* Events */}
      {hasEvents &&
        registrationGroups.map(
          (group, index) =>
            group.priceUnitAmount > 0 && (
              <div key={group.id} className="flex flex-col gap-1.5">
                <OrderItemRow
                  label={
                    group.sessionCount > 1
                      ? pluralize({ count: group.sessionCount, singular: 'session' })
                      : `Event #${index + 1}`
                  }
                  highlightLabel={group.title}
                  amount={group.priceUnitAmount}
                />
                {(() => {
                  const discount = groupDiscountMap.get(group.id);
                  if (!discount || discount.amount <= 0) return null;
                  return (
                    <OrderItemRow
                      label={`Discount: ${
                        discount.source === EventCouponSourceEnum.EarlyBird
                          ? 'Early bird'
                          : discount.name
                      }`}
                      amount={-discount.amount}
                    />
                  );
                })()}
              </div>
            ),
        )}

      {/* Registration fee */}
      {hasRegistrationFee && <OrderItemRow label="Registration fee" amount={registrationFee} />}

      {/* Platform Fee */}
      {hasPlatformFee && (
        <OrderItemRow
          label="Platform Fee"
          amount={platformFeeUnitAmount}
          tooltipId={TOOLTIP_ID_PLATFORM_FEE}
          tooltipContent={platformFeeTooltip}
        />
      )}

      {/* Discount */}
      {hasDiscount && (
        <OrderItemRow
          label={`Discount: ${
            appliedCoupon?.source === EventCouponSourceEnum.EarlyBird
              ? 'Early bird'
              : appliedCoupon?.name || ''
          }`}
          amount={-discountUnitAmount}
        />
      )}

      {/* Taxes and credit card fees */}
      {hasProcessingFee && (
        <OrderItemRow
          label="Taxes and credit cards fees"
          amount={Math.round(processingFee)}
          tooltipId={TOOLTIP_ID_TAXES_FEE}
          tooltipContent={taxesTooltip}
        />
      )}

      {/* Total */}
      {hasTotal && <OrderItemRow label="Total" amount={total} isTotal />}
    </div>
  );
}
